import React from 'react';
import RecordMeltActions from './RecordMeltActions';
import './RecordMeltBar.css';

/**
 * The condensed bar a record page shows once its header scrolls under the topbar, in the shape
 * EventDetail's melt bar set (`.record-header-melt` in global.css): identity, actions, save state,
 * the stage tracker and an optional workspace slot. The page decides when it shows (pass the
 * result of `useHeaderMelt` on the header) and what goes in it.
 *
 * While a page edits its record, pass `editing` with Save/Cancel as the primary actions: the bar
 * then stands in for StickyActionBar's desktop strip (render that with `strip={false}`), and on
 * phones the actions give way to StickyActionBar's bottom bar.
 *
 * Pass the header as `anchorRef` too: its left and right edges are the page column, which the bar
 * keeps to where the top bar is a side rail (Wayfinder from 1200px, RecordMeltBar.css).
 */
export default function RecordMeltBar({
  melted,
  ariaLabel,
  title,
  stage,
  meta,
  total,
  primaryActions,
  secondaryActions,
  moreLabel,
  actionsLabel,
  editing = false,
  saveIndicator,
  progress,
  slotRef,
  anchorRef,
}: {
  melted: boolean;
  ariaLabel: string;
  title: React.ReactNode;
  /** The stage pill; `key` feeds `data-stage` for the stage tints. */
  stage?: { key: string; label: string } | null;
  /** A short muted note after the stage, e.g. the customer. Hidden on phones. */
  meta?: React.ReactNode;
  /** A money total, shown like Events' grand total. */
  total?: React.ReactNode;
  primaryActions: React.ReactNode;
  /** Inline on wide screens, in a "More" menu below 1320px. */
  secondaryActions?: React.ReactNode;
  moreLabel?: string;
  actionsLabel?: string;
  editing?: boolean;
  saveIndicator?: React.ReactNode;
  progress?: React.ReactNode;
  /** Ref for the workspace slot, for a MeltBarSlotProvider (melted grid column headers). */
  slotRef?: React.Ref<HTMLDivElement>;
  /** The record header, whose edges give the page column. */
  anchorRef?: React.RefObject<HTMLElement | null>;
}) {
  const barRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const bar = barRef.current;
    const anchor = anchorRef?.current;
    if (!melted || !bar || !anchor) return undefined;
    const measure = () => {
      const column = anchor.getBoundingClientRect();
      bar.style.setProperty('--record-melt-left', `${Math.round(column.left)}px`);
      bar.style.setProperty('--record-melt-right', `${Math.round(document.documentElement.clientWidth - column.right)}px`);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(anchor);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [melted, anchorRef]);
  if (!melted) return null;
  return (
    <div ref={barRef} className="record-header-melt record-melt-bar" role="navigation" aria-label={ariaLabel}>
      <div className="record-header-melt__identity">
        <span className="record-header-melt__title">{title}</span>
        {stage ? <span className="record-header-melt__stage" data-stage={stage.key}>{stage.label}</span> : null}
        {meta ? <span className="record-header-melt__meta">{meta}</span> : null}
        {total ? <span className="record-header-melt__grand-total" title="Grand total">{total}</span> : null}
      </div>
      <div className={`record-header-melt__actions${editing ? ' record-header-melt__actions--editing' : ''}`} role={actionsLabel ? 'group' : undefined} aria-label={actionsLabel}>
        <RecordMeltActions primary={primaryActions} secondary={secondaryActions} moreLabel={moreLabel} />
      </div>
      {saveIndicator ? <div className="record-header-melt__save-indicator">{saveIndicator}</div> : null}
      {progress ? <div className="record-header-melt__progress">{progress}</div> : null}
      {slotRef ? <div ref={slotRef} className="record-header-melt__workspace-slot" /> : null}
    </div>
  );
}
